import { BackfillProgress, type Job } from "@malfak/contracts";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { ArrowUpFromLine, Bot, Download, Layers, RefreshCcw, Sparkles, type LucideIcon } from "lucide-react";
import { fmtNumber } from "@/lib/format";

export type JobStatus = Job["status"];
export type JobsPage = { items: Job[]; nextCursor: string | null };

export const ACTIVE_STATUSES: readonly JobStatus[] = ["queued", "running", "paused"];
export const isActive = (s: string) => s === "queued" || s === "running" || s === "paused";
export const isTerminal = (s: string) => s === "succeeded" || s === "failed" || s === "cancelled";

export const CATEGORY_ICON: Record<Job["category"], LucideIcon> = { sync: RefreshCcw, bulk: Layers, ai: Sparkles, publish: ArrowUpFromLine, export: Download, other: Bot };

export const STATUS_TONE: Record<JobStatus, "accent" | "ok" | "warn" | "bad" | "neutral" | "info"> = {
  queued: "neutral",
  running: "accent",
  paused: "warn",
  succeeded: "ok",
  failed: "bad",
  cancelled: "neutral",
};

/** Ring/bar colour per status (design tokens only). */
export const STATUS_STROKE: Record<JobStatus, string> = {
  queued: "var(--ink-3)",
  running: "var(--accent)",
  paused: "var(--warn)",
  succeeded: "var(--ok)",
  failed: "var(--bad)",
  cancelled: "var(--ink-3)",
};

/** ALP-77: the initial import after Instagram Login. */
export const BACKFILL_KIND = "backfill_account";

/** A backfill job's `result` as BackfillProgress, or null (other kinds, or no/partial result yet). */
export function backfillProgressOf(job: Pick<Job, "kind" | "result"> | null | undefined): BackfillProgress | null {
  if (!job || job.kind !== BACKFILL_KIND) return null;
  const r = BackfillProgress.safeParse(job.result);
  return r.success ? r.data : null;
}

/** "Posts 40/312 · Comments 1,850 · Messages 120" (localized, locale digits). */
export function backfillLine(p: BackfillProgress, t: TFunction): string {
  const posts = p.posts.total
    ? t("oauth.backfill.line.postsOf", { done: fmtNumber(p.posts.done), total: fmtNumber(p.posts.total) })
    : t("oauth.backfill.line.posts", { done: fmtNumber(p.posts.done) });
  return [posts, t("oauth.backfill.line.comments", { done: fmtNumber(p.comments.done) }), t("oauth.backfill.line.messages", { done: fmtNumber(p.messages) })].join(" · ");
}

/** Localized label: kind title + target label ("Reload comments · Autumn drop"); falls back to the API's English title. */
export function jobLabel(job: Pick<Job, "kind" | "title" | "target">, t: TFunction): string {
  if (job.kind === BACKFILL_KIND) {
    // "Importing @user" (the target label is the account handle)
    const user = (job.target?.label ?? "").replace(/^[\s\u200e@]+/, "");
    return user ? t("oauth.backfill.title", { user }) : t("oauth.backfill.titleGeneric");
  }
  const kind = t(kindKey(job.kind) as "jobs.kinds.resync_post", { defaultValue: "" });
  if (!kind) return job.title;
  return job.target?.label ? `${kind} · ${job.target.label}` : kind;
}

/**
 * i18n key of a job kind. Kinds may contain dots (`comment.reply`, `automation.run.retry`), which i18next reads as
 * nesting, so the locale keys use underscores (`jobs.kinds.comment_reply`). Every JOB_KINDS entry has one (tested).
 */
export const kindKey = (kind: string) => `jobs.kinds.${kind.replace(/\./g, "_")}`;

export function jobKindLabel(kind: string, t: TFunction): string {
  return t(kindKey(kind) as "jobs.kinds.resync_post", { defaultValue: kind.replace(/[._]/g, " ") });
}

/** The single outbound sends (ALP-77: every send is a job). */
export const isSendKind = (kind: string) => kind === "message.send" || kind.startsWith("comment.");

/** "About 3 min left" style ETA, or a hint for queued/paused/indeterminate jobs. */
export function etaText(job: Pick<Job, "status" | "etaSeconds" | "total">, t: TFunction): string | null {
  if (job.status === "queued") return t("jobs.eta.waiting");
  if (job.status === "paused") return t("jobs.eta.paused");
  if (job.status !== "running") return null;
  const s = job.etaSeconds;
  if (s === null || s === undefined) return job.total ? t("jobs.eta.estimating") : null;
  if (s < 60) return t("jobs.eta.soon");
  if (s < 3600) return t("jobs.eta.minutes", { count: Math.round(s / 60) });
  return t("jobs.eta.hours", { count: Math.round(s / 3600) });
}

export function countsText(job: Pick<Job, "done" | "failed" | "skipped" | "total">, t: TFunction): string {
  const processed = job.done + job.failed + job.skipped;
  return job.total ? t("jobs.counts.of", { done: fmtNumber(processed), total: fmtNumber(job.total) }) : t("jobs.counts.done", { count: job.done });
}

type PostLike = { id: string; publications?: { id: string }[] };

/** The post that owns a publication, from any cached post/posts query (the job only knows the publication id). */
export function postIdForPublication(qc: QueryClient, publicationId: string): string | null {
  for (const [, data] of qc.getQueriesData<PostLike | { items: PostLike[] }>({ queryKey: ["posts"] })) {
    const hit = data && "items" in data ? data.items.find((p) => p.publications?.some((x) => x.id === publicationId)) : undefined;
    if (hit) return hit.id;
  }
  for (const [, data] of qc.getQueriesData<PostLike>({ queryKey: ["post"] })) {
    if (data && "publications" in data && data.publications?.some((x) => x.id === publicationId)) return data.id;
  }
  return null;
}

/** Link for the job's target (post page, channels settings, contact profile). */
/** `<Link>` props for a target href: TanStack's `to` is a path only, so the query string goes into `search`. */
export function linkTo(href: string): { to: "/"; search: Record<string, string> } {
  const [path, qs] = href.split("?");
  return { to: path as "/", search: Object.fromEntries(new URLSearchParams(qs ?? "")) };
}

export function targetHref(job: Pick<Job, "target" | "result">, qc?: QueryClient): string | null {
  const tg = job.target;
  if (!tg?.id) return null;
  if (tg.type === "publication") {
    const postId = qc ? postIdForPublication(qc, tg.id) : null;
    return postId ? `/content/posts/${postId}` : "/content/posts";
  }
  if (tg.type === "account") return "/settings/channels";
  if (tg.type === "contact") return `/people/${tg.id}`;
  if (tg.type === "conversation") return `/inbox?conversation=${tg.id}`;
  if (tg.type === "comment") return `/inbox?comment=${tg.id}`;
  if (tg.type === "post") return `/content/posts/${tg.id}`;
  return null;
}

// ---------------------------------------------------------------- cache patches (SSE `operation.updated`)
export type JobEvent = { operationId: string; status: string; done?: number; total?: number | null; failed?: number; kind?: string; error?: string | null; errorCode?: string | null };

function patched(job: Job, e: JobEvent): Job {
  const status = (["queued", "running", "paused", "succeeded", "failed", "cancelled"].includes(e.status) ? e.status : job.status) as JobStatus;
  const done = e.done ?? job.done;
  const failed = e.failed ?? job.failed;
  const total = e.total === undefined ? job.total : e.total;
  const processed = done + failed + job.skipped;
  const progress = status === "succeeded" ? 1 : total ? Math.min(1, processed / total) : null;
  const live = isActive(status);
  return {
    ...job, status, done, failed, total, progress, etaSeconds: status === "running" ? job.etaSeconds : null,
    ...(e.error !== undefined ? { error: e.error } : {}), ...(e.errorCode !== undefined ? { errorCode: e.errorCode } : {}),
    finishedAt: live ? job.finishedAt : (job.finishedAt ?? new Date().toISOString()),
    can: live ? job.can : { ...job.can, cancel: false, pause: false, resume: false },
  };
}

/**
 * Apply an `operation.updated` event to every cached `job` and `jobs` entry for that id (the global SSE sync uses the app
 * query client; components pass their own via useQueryClient). Returns true when the job was found in a cache.
 */
export function patchJobCaches(qc: QueryClient, e: JobEvent): boolean {
  let found = false;
  qc.setQueriesData<Job>({ queryKey: ["job"] }, (old) => {
    if (!old || old.id !== e.operationId) return old;
    found = true;
    return patched(old, e);
  });
  qc.setQueriesData<JobsPage | InfiniteData<JobsPage>>({ queryKey: ["jobs"] }, (old) => {
    if (!old) return old;
    const map = (items: Job[]) =>
      items.map((j) => {
        if (j.id !== e.operationId) return j;
        found = true;
        return patched(j, e);
      });
    if ("pages" in old) return { ...old, pages: old.pages.map((p) => ({ ...p, items: map(p.items) })) };
    return { ...old, items: map(old.items) };
  });
  return found;
}

const timers = new WeakMap<QueryClient, ReturnType<typeof setTimeout>>();
const pendingIds = new WeakMap<QueryClient, Set<string>>();

/**
 * Throttled refetch after progress events (ETA and server-side controls come from the API): at most one refetch per
 * window, and never starved by a steady stream of events (a debounce would wait until the job ends).
 */
export function refreshJobsSoon(qc: QueryClient, id?: string, delayMs = 600) {
  let ids = pendingIds.get(qc);
  if (!ids) pendingIds.set(qc, (ids = new Set()));
  if (id) ids.add(id);
  if (timers.get(qc) !== undefined) return;
  timers.set(
    qc,
    setTimeout(() => {
      timers.delete(qc);
      const list = [...(pendingIds.get(qc) ?? [])];
      pendingIds.get(qc)?.clear();
      void qc.invalidateQueries({ queryKey: ["jobs"] });
      void qc.invalidateQueries({ queryKey: ["jobCounts"] });
      for (const jobId of list) void qc.invalidateQueries({ queryKey: ["job"], predicate: (q) => (q.queryKey[2] as { jobId?: string } | null)?.jobId === jobId });
    }, delayMs),
  );
}
